import type { SchoolRecord } from '@/models';
import { useActiveSchool, useGlobalState } from './useGlobalState';
import { useSchools } from './school';

export interface CurrentSchool {
  schools: SchoolRecord[];
  isLoading: boolean;
  error: unknown;
  /** The default school: the organization's first school. */
  defaultSchoolId: number | null;
  /** The school chosen for this session, or `''` to follow the default. */
  chosenSchoolId: number | '';
  setChosenSchoolId: (schoolId: number | '') => void;
  /** The school pages should work in: the session choice, else the default. */
  currentSchoolId: number | null;
  currentSchool: SchoolRecord | null;
}

/**
 * The school the app is working in, shared across pages. The Foundation API
 * scopes classes, sections, subjects, academic years, grading and the calendar
 * to a school; school-scoped hooks read `currentSchoolId`. A session choice
 * that no longer exists (deleted, or another organization's) is ignored.
 */
export function useCurrentSchool(): CurrentSchool {
  const { data: schools = [], isLoading, error } = useSchools();
  const [storedSchoolId, setChosenSchoolId] = useActiveSchool();

  const { state } = useGlobalState();
  const authenticatedSchool = state.access
    ? schools.find((s) => s.branchId === state.access?.branchId)
    : null;
  const defaultSchoolId = state.access
    ? (authenticatedSchool?.schoolId ?? null)
    : (schools[0]?.schoolId ?? null);
  const chosenSchoolId = schools.some((s) => s.schoolId === storedSchoolId) ? storedSchoolId : '';
  const currentSchoolId = state.access ? defaultSchoolId : chosenSchoolId || defaultSchoolId;

  return {
    schools,
    isLoading,
    error,
    defaultSchoolId,
    chosenSchoolId,
    setChosenSchoolId,
    currentSchoolId,
    currentSchool: schools.find((s) => s.schoolId === currentSchoolId) ?? null,
  };
}
